import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const BulkActionsPanel = ({ 
  selectedItems = [], 
  onBulkAction = () => {},
  onClearSelection = () => {},
  totalItems = 0
}) => {
  const [showActions, setShowActions] = useState(false);
  const [bulkPriority, setBulkPriority] = useState('');

  const priorityOptions = [
    { value: 'high', label: 'High Priority' },
    { value: 'medium', label: 'Medium Priority' },
    { value: 'low', label: 'Low Priority' }
  ];

  const handleBulkPriorityChange = () => {
    if (bulkPriority && selectedItems?.length > 0) {
      onBulkAction('changePriority', { priority: bulkPriority, items: selectedItems });
      setBulkPriority('');
    }
  };

  const handleBulkRemove = () => {
    if (selectedItems?.length > 0 && window.confirm(`Remove ${selectedItems?.length} items from wishlist?`)) {
      onBulkAction('remove', { items: selectedItems });
    }
  };

  const handleBulkToggleAlerts = () => {
    if (selectedItems?.length > 0) {
      onBulkAction('toggleAlerts', { items: selectedItems });
    }
  };

  const handleExportSelected = () => {
    if (selectedItems?.length > 0) {
      onBulkAction('export', { items: selectedItems });
    }
  };

  const handleShareSelected = () => {
    if (selectedItems?.length > 0) {
      onBulkAction('share', { items: selectedItems });
    }
  };

  const handleSelectAll = () => {
    onBulkAction('selectAll');
  };

  const handleDeselectAll = () => {
    onClearSelection();
  };

  if (selectedItems?.length === 0) {
    return null;
  }

  return (
    <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6 animate-fade-in">
      <div className="flex items-center justify-between">
        {/* Selection Info */}
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Icon name="CheckSquare" size={20} color="var(--color-accent)" />
            <span className="font-medium text-foreground">
              {selectedItems?.length} of {totalItems} selected
            </span>
          </div>
          
          <div className="hidden sm:flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={selectedItems?.length === totalItems ? handleDeselectAll : handleSelectAll}
            >
              {selectedItems?.length === totalItems ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              iconName="X"
              iconPosition="left"
            >
              Clear
            </Button>
          </div>
        </div>

        {/* Toggle Actions */}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowActions(!showActions)}
          iconName={showActions ? "ChevronUp" : "ChevronDown"}
          iconPosition="right"
        >
          Actions
        </Button>
      </div>
      {/* Bulk Actions */}
      {showActions && (
        <div className="mt-4 pt-4 border-t border-accent/20 animate-fade-in">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Priority Change */}
            <div className="flex items-end space-x-2">
              <div className="flex-1">
                <Select
                  label="Change Priority"
                  options={priorityOptions}
                  value={bulkPriority}
                  onChange={setBulkPriority}
                  placeholder="Select priority..."
                />
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkPriorityChange}
                disabled={!bulkPriority}
                iconName="Flag"
                iconPosition="left"
              >
                Apply
              </Button>
            </div>

            {/* Quick Actions */}
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleBulkToggleAlerts}
                iconName="Bell"
                iconPosition="left"
              >
                Toggle Alerts
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportSelected}
                iconName="Download"
                iconPosition="left"
              >
                Export
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleShareSelected}
                iconName="Share2"
                iconPosition="left"
              >
                Share
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkRemove}
                iconName="Trash2"
                iconPosition="left"
              >
                Remove
              </Button>
            </div>
          </div>

          {/* Mobile Actions */}
          <div className="sm:hidden mt-4 flex space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={selectedItems?.length === totalItems ? handleDeselectAll : handleSelectAll}
              className="flex-1"
            >
              {selectedItems?.length === totalItems ? 'Deselect All' : 'Select All'}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearSelection}
              iconName="X"
              iconPosition="left"
              className="flex-1"
            >
              Clear Selection
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkActionsPanel;